<template>
  <div class="address-display-wrapper">
    <div
      class="address-display"
      :class="{ 'address-display--copied': copied }"
      @click="copyToClipboard"
      :title="fullAddress"
    >
      <code class="address-display__text">
        {{ displayAddress }}
      </code>
      <span v-if="showCopyHint" class="address-display__hint">
        {{ copied ? $t('common.copied') : $t('common.tapToCopy') }}
      </span>
    </div>

    <!-- QR Button -->
    <button
      v-if="showQrButton"
      type="button"
      class="qr-button"
      @click="openQrModal"
      :title="$t('common.showQr')"
    >
      <QrCodeIcon :size="18" />
    </button>

    <!-- QR Modal -->
    <BaseModal
      :open="isQrModalOpen"
      @close="closeQrModal"
      max-width="400px"
    >
      <template #header>
        <h3 class="modal-title">{{ $t('common.qrCode') }}</h3>
      </template>

      <div class="qr-modal-content">
        <div class="qr-container">
          <canvas ref="qrCanvas" class="qr-canvas"></canvas>
          <div v-if="qrLoading" class="qr-loading">
            <div class="spinner"></div>
          </div>
        </div>

        <div class="full-address">
          <code>{{ address }}</code>
        </div>

        <button
          type="button"
          class="download-button"
          @click="downloadQR"
        >
          <Download :size="16" />
          {{ $t('common.download') }}
        </button>
      </div>
    </BaseModal>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { QrCode as QrCodeIcon, Download } from 'lucide-vue-next';
import QRCode from 'qrcode';
import BaseModal from '../ui/BaseModal.vue';

const { t: $t } = useI18n();

const props = defineProps({
  address: {
    type: String,
    required: true
  },
  truncate: {
    type: Boolean,
    default: true
  },
  startChars: {
    type: Number,
    default: 6
  },
  endChars: {
    type: Number,
    default: 4
  },
  showCopyHint: {
    type: Boolean,
    default: false
  },
  showQrButton: {
    type: Boolean,
    default: false
  }
});

const copied = ref(false);
const isQrModalOpen = ref(false);
const qrLoading = ref(false);
const qrCanvas = ref(null);

let copiedTimeout = null;

const displayAddress = computed(() => {
  if (!props.truncate || props.address.length <= props.startChars + props.endChars) {
    return props.address;
  }
  return `${props.address.slice(0, props.startChars)}...${props.address.slice(-props.endChars)}`;
});

const fullAddress = computed(() => props.address);

async function copyToClipboard() {
  if (copied.value) return;

  try {
    await navigator.clipboard.writeText(props.address);
    copied.value = true;

    if (copiedTimeout) {
      clearTimeout(copiedTimeout);
    }
    copiedTimeout = setTimeout(() => {
      copied.value = false;
    }, 2000);
  } catch (err) {
    console.error('Failed to copy address:', err);
  }
}

// QR Code functions
async function generateQR() {
  if (!props.showQrButton || !props.address) return;

  try {
    qrLoading.value = true;
    if (qrCanvas.value) {
      await QRCode.toCanvas(qrCanvas.value, props.address, {
        width: 280,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
    }
  } catch (error) {
    console.error('Failed to generate QR code:', error);
  } finally {
    qrLoading.value = false;
  }
}

function openQrModal() {
  isQrModalOpen.value = true;
  // Generate QR when modal opens
  setTimeout(() => generateQR(), 50);
}

function closeQrModal() {
  isQrModalOpen.value = false;
}

function downloadQR() {
  if (!qrCanvas.value) return;

  try {
    const canvas = qrCanvas.value;
    const url = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.download = `qr-${props.address.slice(0, 8)}.png`;
    link.href = url;
    link.click();
  } catch (error) {
    console.error('Failed to download QR code:', error);
  }
}

// Watch address changes to regenerate QR
watch(() => props.address, () => {
  if (isQrModalOpen.value) {
    generateQR();
  }
});

// Generate QR on mount if modal is open
onMounted(() => {
  if (props.showQrButton && isQrModalOpen.value) {
    generateQR();
  }
});
</script>

<style scoped>
.address-display-wrapper {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.address-display {
  display: inline-flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.address-display__text {
  font-family: 'Monaco', 'Courier New', monospace;
  font-size: 14px;
  font-weight: 500;
  color: inherit;
  white-space: nowrap;
  transition: color 0.2s ease;
}

.address-display__hint {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 12px;
  color: currentColor;
  opacity: 0.7;
  font-weight: 400;
  transition: all 0.2s ease;
}

.address-display--copied .address-display__hint {
  opacity: 1;
  font-weight: 500;
}

/* QR Button */
.qr-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  color: currentColor;
  cursor: pointer;
  transition: all 0.2s ease;
}

.qr-button:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-1px);
}

.qr-button:active {
  transform: translateY(0);
}

/* Modal */
.modal-title {
  font-size: 18px;
  font-weight: 600;
  color: #182855;
  margin: 0;
}

.qr-modal-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.qr-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  border-radius: 12px;
  background: white;
  border: 2px solid #e5e7eb;
}

.qr-canvas {
  display: block;
  border-radius: 8px;
}

.qr-loading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 12px;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e5e7eb;
  border-top-color: #182855;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.full-address {
  width: 100%;
  padding: 12px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  text-align: center;
  word-break: break-all;
}

.full-address code {
  font-family: 'Monaco', 'Courier New', monospace;
  font-size: 12px;
  color: #475569;
}

.download-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px 20px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f8fafc;
  color: #182855;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.download-button:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.download-button:active {
  transform: scale(0.98);
}
</style>
